import { FormIcon, ReplyIcon, ThreadIcon, cn } from '@playerbase/ui';

import { ReactionPill } from './feedback-row.js';
import type { FormSubmissionEntry } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Compact activity-panel entry for a form submission.
//
// Mirrors the look of `activity-item.tsx` (one source label band + a
// short body) but renders a form-submission-specific shape rather than
// a `FeedbackRow`. When the bot has captured the summary message_id,
// engagement (reactions / replies / thread messages) gathered on the
// Discord post surfaces here as compact counters so an active
// conversation around a submission is visible at a glance.
// ---------------------------------------------------------------------------

interface FormSubmissionActivityItemProps {
  entry: FormSubmissionEntry;
  channelLabel: string | null;
  onSelect: () => void;
  unread: boolean;
}

const ONE_MINUTE_MS = 60_000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

function formatRelativePast(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  if (diff < ONE_MINUTE_MS) return 'just now';
  if (diff < ONE_HOUR_MS) return `${Math.floor(diff / ONE_MINUTE_MS)}m ago`;
  if (diff < ONE_DAY_MS) return `${Math.floor(diff / ONE_HOUR_MS)}h ago`;
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function FormSubmissionActivityItem({
  entry,
  channelLabel,
  onSelect,
  unread,
}: FormSubmissionActivityItemProps): JSX.Element {
  const submitter = entry.submitterHandle ?? 'Anonymous';
  const channelTail = channelLabel === null ? '' : ` · #${channelLabel}`;
  const engagement = entry.engagement;
  const hasReactions = engagement !== null && engagement.reactions.length > 0;
  const hasReplies = engagement !== null && engagement.replyCount > 0;
  const hasThread = engagement !== null && engagement.threadReplyCount > 0;
  const hasEngagement = hasReactions || hasReplies || hasThread;
  // Engagement-driven rows want the timestamp of the latest reaction /
  // reply, not the original submission time, so the row reads as "this
  // is hot right now" rather than "submitted ages ago".
  const timestamp = hasEngagement ? entry.lastActivityAt : entry.submittedAt;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'block cursor-pointer transition-colors',
        // Same hover shape as ActivityItem so a tile change reads
        // identically across activity sources.
        'hover:bg-surface-3',
        'focus:outline-none focus-visible:bg-surface-3',
      )}
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-2 text-xs text-text-muted">
        <span className="truncate">
          form submission{channelTail}
        </span>
        {unread && (
          <span
            aria-hidden
            className="block h-2 w-2 shrink-0 rounded-full bg-accent motion-safe:animate-glow"
          />
        )}
      </div>
      <div className="flex items-start gap-2 px-4 pb-3 pt-1">
        <FormIcon className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-text">
            <span className="font-medium">{submitter}</span> submitted to{' '}
            <span className="font-medium">{entry.formTitle}</span>
          </p>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
            <span>{formatRelativePast(timestamp)}</span>
            {(hasReplies || hasThread) && (
              <span className="flex items-center gap-2">
                {hasReplies && (
                  <span className="inline-flex items-center gap-1">
                    <ReplyIcon className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{engagement!.replyCount}</span>
                  </span>
                )}
                {hasThread && (
                  <span className="inline-flex items-center gap-1">
                    <ThreadIcon className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{engagement!.threadReplyCount}</span>
                  </span>
                )}
              </span>
            )}
          </div>
          {hasReactions && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {engagement!.reactions.map((r) => (
                <ReactionPill key={r.identifier} reaction={r} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
